import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { AuthService } from '@services/auth/auth.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { filter, map, of, switchMap, take, tap } from 'rxjs';
import { UserServiceV2 } from '../services/user-service/user.service';

export const permissionGuard: (permissions: string[]) => CanActivateFn = (permissions: string[]) => (route, state) => {
    const userService = inject(UserServiceV2);
    const authService = inject(AuthService);
    const snackbarService = inject(SnackbarService);
    return authService.isDoneLoading$.pipe(
        filter((isDone) => isDone),
        switchMap(() => {
            const authServiceHasValidTokenForUser = authService.hasValidAccessToken && authService.hasValidIdToken;
            if (!authServiceHasValidTokenForUser) {
                // Store the original URL so when users signs-in in B2C and is redirected back to app,
                // we can move them to the page they originally wanted to visit.
                // Do it only if user is not signed in yet (but not when he is signed and not allowed) so we
                // don't end up in a redirect loop.
                localStorage.setItem('redirectUrl', state.url);
                return of({ authorized: false, hasPermission: false });
            }
            return userService.hasAnyPermission$(permissions).pipe(
                take(1),

                map((hasPermission) => {
                    return { authorized: true, hasPermission: hasPermission };
                })
            );
        }),
        tap((params) => {
            if (!params.authorized || !params.hasPermission) {
                authService.login(state.url);
            }
            if (!params.hasPermission) {
                snackbarService.openErrorSnackbar('The requested page could not be found.');
            }
        }),
        map((params) => params.hasPermission && params.authorized),
        take(1)
    );
};
